/**
 * Рефералы (§40.5, §51).
 *
 * Подозрительные приглашения ждут решения человека: автоматика их только
 * помечает, начисление не делает.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminReferralRow } from '@alterium/shared';
import { adminApi } from '../../api/admin';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { useTranslation } from 'react-i18next';

const statuses = ['', 'registered', 'qualified', 'rewarded', 'suspicious', 'revoked'];

export function AdminReferralsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');

  const referrals = useQuery({
    queryKey: ['admin', 'referrals', page, status],
    queryFn: () => adminApi.referrals({ page, status }),
  });

  const resolve = useMutation({
    mutationFn: (params: { id: string; action: 'approve' | 'revoke' }) =>
      adminApi.resolveReferral(params.id, params.action),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'referrals'] }),
  });

  const columns: Array<Column<AdminReferralRow>> = [
    { key: 'referrer', title: t('admin.referral.referrer'), render: (row) => row.referrerEmail },
    { key: 'referred', title: t('admin.referral.referred'), render: (row) => row.referredEmail },
    { key: 'code', title: t('admin.referral.code'), render: (row) => row.code },
    {
      key: 'status',
      title: t('admin.job.status'),
      render: (row) => (
        <span
          className={
            row.status === 'suspicious'
              ? 'text-warning'
              : row.status === 'revoked'
                ? 'text-error'
                : 'text-content'
          }
        >
          {row.status}
        </span>
      ),
    },
    { key: 'rr', title: t('admin.referral.toReferrer'), render: (row) => row.referrerReward, align: 'right' },
    { key: 'rd', title: t('admin.referral.toReferred'), render: (row) => row.referredReward, align: 'right' },
    {
      key: 'note',
      title: t('admin.referral.note'),
      render: (row) => <span className="text-content-secondary">{row.note ?? '—'}</span>,
    },
    {
      key: 'actions',
      title: '',
      render: (row) => (
        <div className="flex gap-2">
          {row.status === 'suspicious' ? (
            <Button
              variant="tertiary"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ id: row.id, action: 'approve' })}
            >{t('admin.referral.approve')}</Button>
          ) : null}
          {row.status !== 'revoked' ? (
            <Button
              variant="tertiary"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate({ id: row.id, action: 'revoke' })}
            >{t('admin.referral.revoke')}</Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <select
        value={status}
        onChange={(event) => {
          setStatus(event.target.value);
          setPage(1);
        }}
        className="h-9 w-56 rounded-control border border-line bg-surface px-3 text-meta text-content focus:border-accent focus:outline-none"
      >
        {statuses.map((value) => (
          <option key={value} value={value}>
            {value || t('admin.allStatuses')}
          </option>
        ))}
      </select>

      <DataTable
        columns={columns}
        rows={referrals.data?.items ?? []}
        rowKey={(row) => row.id}
        total={referrals.data?.total}
        page={page}
        totalPages={referrals.data?.totalPages}
        loading={referrals.isLoading}
        onPage={setPage}
      />
    </div>
  );
}
